<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import router from '../../../../index';
import NextButton from 'dashboard/components-next/button/Button.vue';
import GreetingsEditor from 'shared/components/GreetingsEditor.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import NextSwitch from 'dashboard/components-next/switch/Switch.vue';
import Widget from 'dashboard/modules/widget-preview/components/Widget.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

export default {
  components: {
    GreetingsEditor,
    NextButton,
    Editor,
    SelectInput,
    NextSwitch,
    Widget,
    TabBar,
  },
  data() {
    return {
      inboxName: '',
      channelWebsiteUrl: '',
      channelWidgetColor: '#1F93FF',
      channelWelcomeTitle: '',
      channelWelcomeTagline: '',
      greetingEnabled: false,
      greetingMessage: '',
      selectedDefaultTeamId: '',
      selectedPortalSlug: '',
      selectedCaptainAssistantId: '',
      activeSectionIndex: 0,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      uiFlags: 'inboxes/getUIFlags',
      teams: 'teams/getTeams',
      portals: 'portals/allPortals',
      captainAssistants: 'captainAssistants/getRecords',
    }),
    previewName() {
      return (
        this.inboxName || this.$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.PREVIEW_NAME')
      );
    },
    previewTitle() {
      return (
        this.channelWelcomeTitle ||
        this.$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.PREVIEW_TITLE')
      );
    },
    previewTagline() {
      return (
        this.channelWelcomeTagline ||
        this.$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.PREVIEW_TAGLINE')
      );
    },
    isCaptainEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.CAPTAIN
      );
    },
    sections() {
      return [
        { label: this.$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.TABS.BASICS') },
        { label: this.$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.TABS.APPEARANCE') },
        { label: this.$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.TABS.ROUTING') },
      ];
    },
  },
  mounted() {
    this.$store.dispatch('teams/get');
    this.$store.dispatch('portals/index');
    if (this.isCaptainEnabled) {
      this.$store.dispatch('captainAssistants/get');
    }
  },
  methods: {
    handleSectionChange(section) {
      this.activeSectionIndex = this.sections.findIndex(
        item => item.label === section.label
      );
    },
    async createChannel() {
      try {
        const portalId = this.selectedPortalSlug
          ? this.portals.find(portal => portal.slug === this.selectedPortalSlug)
              ?.id || null
          : null;
        const website = await this.$store.dispatch(
          'inboxes/createWebsiteChannel',
          {
            name: this.inboxName?.trim(),
            default_team_id: this.selectedDefaultTeamId || null,
            portal_id: portalId,
            greeting_enabled: this.greetingEnabled,
            greeting_message: this.greetingMessage,
            channel: {
              type: 'web_widget',
              website_url: this.channelWebsiteUrl,
              widget_color: this.channelWidgetColor,
              welcome_title: this.channelWelcomeTitle,
              welcome_tagline: this.channelWelcomeTagline,
            },
          }
        );

        if (this.selectedCaptainAssistantId) {
          await this.$store.dispatch('captainInboxes/create', {
            assistantId: this.selectedCaptainAssistantId,
            inboxId: website.id,
          });
        }

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: { page: 'new', inbox_id: website.id },
        });
      } catch (error) {
        useAlert(
          error.message ||
            this.$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <div class="h-full w-full col-span-6 overflow-hidden bg-n-slate-2 p-3">
    <form
      class="flex h-full min-h-0 flex-col overflow-hidden rounded-lg bg-n-solid-1 outline outline-1 -outline-offset-1 outline-n-weak"
      @submit.prevent="createChannel"
    >
      <header
        class="flex min-h-16 flex-wrap items-center justify-between gap-3 border-b border-n-weak px-5 py-3"
      >
        <div class="flex min-w-0 items-center gap-3">
          <span
            class="i-lucide-message-circle size-5 shrink-0 text-n-slate-11"
          />
          <div class="min-w-0">
            <h2 class="mb-0 truncate text-base font-semibold text-n-slate-12">
              {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.WORKSPACE_TITLE') }}
            </h2>
            <p class="mb-0 truncate text-xs text-n-slate-10">
              {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.DESC') }}
            </p>
          </div>
        </div>
        <NextButton
          type="submit"
          :is-loading="uiFlags.isCreating"
          :disabled="!channelWebsiteUrl || !inboxName"
          icon="i-lucide-play"
          :label="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.SAVE_AND_CREATE')"
        />
      </header>

      <div class="flex min-h-0 flex-1 flex-col">
        <nav class="border-b border-n-weak px-5 py-3">
          <TabBar
            :tabs="sections"
            :initial-active-tab="activeSectionIndex"
            @tab-changed="handleSectionChange"
          />
        </nav>

        <woot-loading-state
          v-if="uiFlags.isCreating"
          :message="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.LOADING_MESSAGE')"
        />

        <div
          v-else
          class="grid min-h-0 flex-1 grid-cols-1 xl:grid-cols-[minmax(30rem,1fr)_30rem]"
        >
          <main
            class="min-h-0 overflow-y-auto px-6 py-6 xl:border-r border-n-weak"
          >
            <section v-if="activeSectionIndex === 0" class="mx-auto max-w-2xl">
              <div class="mb-5">
                <h3 class="mb-1 text-base font-semibold text-n-slate-12">
                  {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.IDENTITY_TITLE') }}
                </h3>
                <p class="mb-0 text-sm text-n-slate-10">
                  {{
                    $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.IDENTITY_DESCRIPTION')
                  }}
                </p>
              </div>

              <div class="grid grid-cols-1 gap-4 2xl:grid-cols-2">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ $t('INBOX_MGMT.ADD.WEBSITE_NAME.LABEL') }}
                  <input
                    v-model="inboxName"
                    type="text"
                    class="mt-2 mb-0"
                    :placeholder="$t('INBOX_MGMT.ADD.WEBSITE_NAME.PLACEHOLDER')"
                  />
                </label>
                <label class="text-sm font-medium text-n-slate-12">
                  {{
                    $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_DOMAIN.LABEL')
                  }}
                  <input
                    v-model="channelWebsiteUrl"
                    type="text"
                    class="mt-2 mb-0"
                    :placeholder="
                      $t(
                        'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_DOMAIN.PLACEHOLDER'
                      )
                    "
                  />
                </label>
              </div>
              <div
                class="mt-8 flex items-start justify-between gap-6 border-t border-n-weak pt-6"
              >
                <div>
                  <p class="mb-1 text-sm font-medium text-n-slate-12">
                    {{
                      $t(
                        'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.LABEL'
                      )
                    }}
                  </p>
                  <p class="mb-0 text-sm text-n-slate-10">
                    {{
                      $t(
                        'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.HELP_TEXT'
                      )
                    }}
                  </p>
                </div>
                <NextSwitch v-model="greetingEnabled" />
              </div>
              <GreetingsEditor
                v-if="greetingEnabled"
                v-model="greetingMessage"
                class="mt-5 w-full"
                :label="
                  $t(
                    'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_MESSAGE.LABEL'
                  )
                "
                :placeholder="
                  $t(
                    'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_MESSAGE.PLACEHOLDER'
                  )
                "
                richtext
              />
            </section>

            <section v-if="activeSectionIndex === 2" class="mx-auto max-w-2xl">
              <div class="mb-5">
                <h3 class="mb-1 text-base font-semibold text-n-slate-12">
                  {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.ROUTING_TITLE') }}
                </h3>
                <p class="mb-0 text-sm text-n-slate-10">
                  {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.ROUTING_DESCRIPTION') }}
                </p>
              </div>

              <div class="grid grid-cols-1 gap-4 2xl:grid-cols-2">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ $t('INBOX_MGMT.PROGRAM_ROUTING.TEAM.LABEL') }}
                  <SelectInput
                    v-model="selectedDefaultTeamId"
                    class="mt-2"
                    :options="[
                      {
                        value: '',
                        label: $t('INBOX_MGMT.PROGRAM_ROUTING.TEAM.NONE'),
                      },
                      ...teams.map(team => ({
                        value: team.id,
                        label: team.name,
                      })),
                    ]"
                  />
                </label>
                <label class="text-sm font-medium text-n-slate-12">
                  {{ $t('INBOX_MGMT.HELP_CENTER.LABEL') }}
                  <SelectInput
                    v-model="selectedPortalSlug"
                    class="mt-2"
                    :options="[
                      { value: '', label: $t('INBOX_MGMT.HELP_CENTER.NONE') },
                      ...portals.map(portal => ({
                        value: portal.slug,
                        label: portal.name,
                      })),
                    ]"
                  />
                </label>
                <label
                  v-if="isCaptainEnabled"
                  class="text-sm font-medium text-n-slate-12 md:col-span-2"
                >
                  {{ $t('INBOX_MGMT.PROGRAM_ROUTING.CAPTAIN.LABEL') }}
                  <SelectInput
                    v-model="selectedCaptainAssistantId"
                    class="mt-2"
                    :options="[
                      {
                        value: '',
                        label: $t('INBOX_MGMT.PROGRAM_ROUTING.CAPTAIN.NONE'),
                      },
                      ...captainAssistants.map(assistant => ({
                        value: assistant.id,
                        label: assistant.name,
                      })),
                    ]"
                  />
                </label>
              </div>
              <div class="mt-8 border-t border-n-weak pt-6">
                <h4 class="mb-3 text-sm font-semibold text-n-slate-12">
                  {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.READY_TITLE') }}
                </h4>
                <div class="grid gap-3 text-sm text-n-slate-11">
                  <div class="flex items-center gap-2">
                    <span
                      class="i-lucide-check-circle-2 size-4 text-n-teal-10"
                    />
                    {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.READY_ROUTING') }}
                  </div>
                  <div class="flex items-center gap-2">
                    <span
                      class="i-lucide-check-circle-2 size-4 text-n-teal-10"
                    />
                    {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.READY_KNOWLEDGE') }}
                  </div>
                  <div class="flex items-center gap-2">
                    <span class="i-lucide-circle size-4 text-n-slate-8" />
                    {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.READY_INSTALL') }}
                  </div>
                </div>
              </div>
            </section>

            <section v-if="activeSectionIndex === 1" class="mx-auto max-w-2xl">
              <div class="mb-5">
                <h3 class="mb-1 text-base font-semibold text-n-slate-12">
                  {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.APPEARANCE_TITLE') }}
                </h3>
                <p class="mb-0 text-sm text-n-slate-10">
                  {{
                    $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.APPEARANCE_DESCRIPTION')
                  }}
                </p>
              </div>

              <div class="grid grid-cols-1 gap-4 md:grid-cols-[10rem_1fr]">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.WIDGET_COLOR.LABEL') }}
                  <woot-color-picker
                    v-model="channelWidgetColor"
                    class="mt-2"
                  />
                </label>
                <label class="text-sm font-medium text-n-slate-12">
                  {{
                    $t(
                      'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TITLE.LABEL'
                    )
                  }}
                  <input
                    v-model="channelWelcomeTitle"
                    type="text"
                    class="mt-2 mb-0"
                    :placeholder="
                      $t(
                        'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TITLE.PLACEHOLDER'
                      )
                    "
                  />
                </label>
              </div>

              <Editor
                v-model="channelWelcomeTagline"
                :label="
                  $t(
                    'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TAGLINE.LABEL'
                  )
                "
                :placeholder="
                  $t(
                    'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TAGLINE.PLACEHOLDER'
                  )
                "
                :max-length="255"
                channel-type="Context::InboxSettings"
                class="mt-5"
              />
            </section>
          </main>

          <aside
            class="hidden min-h-0 overflow-y-auto bg-n-slate-2/50 px-5 py-5 xl:block"
          >
            <div class="flex items-center justify-between mb-3">
              <div>
                <h3 class="mb-0 text-sm font-semibold text-n-slate-12">
                  {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.PREVIEW') }}
                </h3>
                <p class="mb-0 text-xs text-n-slate-10">
                  {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.PREVIEW_DESCRIPTION') }}
                </p>
              </div>
              <span
                class="inline-flex items-center rounded-md bg-n-teal-3 px-2 py-0.5 text-xs font-medium text-n-teal-11 outline outline-1 -outline-offset-1 outline-n-teal-5"
              >
                {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.LIVE') }}
              </span>
            </div>
            <div
              class="flex min-h-[43rem] flex-col overflow-hidden rounded-lg bg-n-solid-1 px-3 pb-6 pt-3 outline outline-1 -outline-offset-1 outline-n-weak"
            >
              <Widget
                :welcome-heading="previewTitle"
                :welcome-tagline="previewTagline"
                :website-name="previewName"
                is-online
                reply-time="in_a_few_minutes"
                :color="channelWidgetColor"
                widget-bubble-position="right"
                widget-bubble-type="standard"
              />
            </div>
          </aside>
        </div>
      </div>
    </form>
  </div>
</template>
